import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Context } from './context';
import { DragDirectionEnum } from './types';
export function useDndItem({ ref, index, }) {
    const { type, isAnyDragging, setIsAnyDragging, items, moveItem, onDragStarts, onDragEnds, itemPlaceholder, options: { customDrop = false, customSource = false, customPreview = false, direction = DragDirectionEnum.Vertical, } = {}, } = React.useContext(Context);
    if (!type || !items || !moveItem || !setIsAnyDragging) {
        throw Error('no DndContext provider found, nest your component to packages/components/Dnd/List');
    }
    const [, drop] = useDrop({
        accept: type,
        hover(draggingItem, monitor) {
            const dragIndex = draggingItem.index;
            const hoverIndex = index;
            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }
            // Determine mouse position
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) {
                return;
            }
            // Determine rectangle on screen
            const hoverBoundingRect = ref.current.getBoundingClientRect();
            let isBeforeHovered = false;
            switch (direction) {
                case DragDirectionEnum.Vertical: {
                    // Get vertical middle
                    const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
                    // Get pixels to the top
                    const hoverClientY = clientOffset.y - hoverBoundingRect.top;
                    /*
                     * Only perform the move when the mouse has crossed half of the items height
                     * When dragging downwards, only move when the cursor is below 50%
                     * When dragging upwards, only move when the cursor is above 50%
                     * Dragging downwards
                     */
                    isBeforeHovered = hoverClientY < hoverMiddleY;
                    break;
                }
                case DragDirectionEnum.Horizontal: {
                    // Get vertical middle
                    const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
                    // Get pixels to the top
                    const hoverClientX = clientOffset.x - hoverBoundingRect.left;
                    /*
                     * Only perform the move when the mouse has crossed half of the items width
                     * When dragging downwards, only move when the cursor is below 50%
                     * When dragging upwards, only move when the cursor is above 50%
                     * Dragging downwards
                     */
                    isBeforeHovered = hoverClientX < hoverMiddleX;
                    break;
                }
                default:
                    throw Error('invalid drag direction');
            }
            // Dragging upwards
            if (dragIndex < hoverIndex === isBeforeHovered) {
                return;
            }
            moveItem(dragIndex, hoverIndex);
            /*
             * Note: we're mutating the monitor item here!
             * Generally it's better to avoid mutations,
             * but it's good here for the sake of performance
             * to avoid expensive index searches.
             */
            // eslint-disable-next-line no-param-reassign
            draggingItem.index = hoverIndex;
        },
    });
    const item = (items === null || items === void 0 ? void 0 : items[index]) || itemPlaceholder;
    if (!item) {
        throw Error('index overflow or itemPlaceholder not provided');
    }
    const [{ isDragging }, dragSource, dragPreview] = useDrag({
        type,
        item: () => {
            setIsAnyDragging(true);
            onDragStarts === null || onDragStarts === void 0 ? void 0 : onDragStarts();
            return { id: item.id, index };
        },
        collect: monitor => ({
            isDragging: monitor.isDragging(),
        }),
        end: () => {
            setIsAnyDragging(false);
            onDragEnds === null || onDragEnds === void 0 ? void 0 : onDragEnds();
        },
    });
    React.useEffect(() => {
        if (!customDrop) {
            drop(ref);
        }
    }, [drop, customDrop, ref]);
    React.useEffect(() => {
        if (!customSource) {
            dragSource(ref);
        }
    }, [dragSource, customSource, ref]);
    React.useEffect(() => {
        if (!customPreview) {
            dragPreview(ref);
        }
    }, [dragPreview, customPreview, ref]);
    return {
        drop,
        dragSource,
        dragPreview,
        item,
        isAnyDragging,
        isDragging,
    };
}
//# sourceMappingURL=data:application/json;base64,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